import { z } from "zod";

const StructuredErrorSchema = z.object({
    name: z.string().optional(),
    message: z.string(),
});
const ZodIssuesSchema = z.array(z.object({ message: z.string() })).min(1);

/**
 * `usePost`/`usePut`/`usePatch`/`useDelete` reject with the parsed error response body rather
 * than an `Error`, so passing the rejection value straight to `window.alert` or a toast
 * renders "[object Object]". This extracts the human-readable message from it.
 *
 * The API types `error` as a string, but schema validation failures come back as a structured
 * `{ name: "ZodError", message }` object, where `message` is itself a JSON array of Zod issues.
 * Auth middleware rejections come back with an empty `error` and fall through to the fallback.
 */
export function toApiErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === "object" && error !== null && "error" in error) {
        const detail = (error as { error: unknown }).error;

        if (typeof detail === "string" && detail.length > 0) {
            return detail;
        }

        const structured = StructuredErrorSchema.safeParse(detail).data;
        if (structured) {
            //TODO: Handle this in the BE.
            return structured.name === "ZodError"
                ? flattenZodIssues(structured.message)
                : structured.message;
        }
    }

    if (error instanceof Error && error.message.length > 0) {
        return error.message;
    }

    return fallback;
}

/**
 * A ZodError's `message` is a JSON array of issues, so join their individual messages.
 * Falls back to the raw message if it doesn't parse into that array.
 */
function flattenZodIssues(message: string): string {
    const issues = ZodIssuesSchema.safeParse(safeJsonParse(message)).data;
    return issues ? issues.map((issue) => issue.message).join(", ") : message;
}

function safeJsonParse(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
}
